/**
 * useDeepSea - 深海領域管理フック
 *
 * 潜航/浮上の操作、深海領域の状態管理、モーダル制御を担当
 */

import { useState, useCallback, useMemo } from 'react';
import type { HandCard, CardWithId, MonsterInstance } from '../../types/card';
import type { PlayerNumber } from '../../types/player';
import type { DeepSeaZone, DeepSeaCard, SurfaceBonus, SubmergeContext, SurfaceContext } from '../../engine/deepSeaZone/types';
import {
  createDeepSeaZone,
  unlockDeepSeaZone,
  setDeepSeaCapacity,
  canSubmerge,
  submergeCard,
  canSurface,
  surfaceCard,
  incrementDepth,
  getSurfaceCost,
  hasSenkouKeyword,
  hasDeepSeaCapacity,
  hasAnkokuSuiikiKeyword,
} from '../../engine/deepSeaZone/deepSeaManager';
import { getDepthBonus, calculateSurfaceCost } from '../../engine/deepSeaZone/constants';

// ========================================
// Hook戻り値の型定義
// ========================================

export interface DeepSeaReturn {
  // State
  p1DeepSeaZone: DeepSeaZone;
  p2DeepSeaZone: DeepSeaZone;
  showDeepSeaModal: PlayerNumber | null;

  // Setters
  setP1DeepSeaZone: React.Dispatch<React.SetStateAction<DeepSeaZone>>;
  setP2DeepSeaZone: React.Dispatch<React.SetStateAction<DeepSeaZone>>;
  setShowDeepSeaModal: React.Dispatch<React.SetStateAction<PlayerNumber | null>>;

  // Actions
  /** 深海領域を開放 */
  unlockDeepSea: (player: PlayerNumber) => void;
  /** カードを潜航させる（手札から深海領域へ） */
  handleSubmerge: (
    player: PlayerNumber,
    card: HandCard,
    turn: number,
    initialDepth?: number,
    removeFromHand?: (card: HandCard) => void,
    addLog?: (message: string, type?: 'info' | 'damage' | 'heal') => void
  ) => boolean;
  /** カードを浮上させる（深海領域からフィールドへ） */
  handleSurface: (
    player: PlayerNumber,
    deepSeaIndex: number,
    fieldSlot: number,
    costReduction: number,
    placeOnField: (card: CardWithId, slot: number, bonus: SurfaceBonus) => boolean,
    deductSP: (amount: number) => void,
    addLog?: (message: string, type?: 'info' | 'damage' | 'heal') => void
  ) => boolean;
  /** 深度を増加（エンドフェイズ） */
  handleIncrementDepth: (
    player: PlayerNumber,
    bonusDepth?: number,
    addLog?: (message: string, type?: 'info' | 'damage' | 'heal') => void
  ) => void;
  /** 深海領域の上限を変更 */
  setCapacity: (player: PlayerNumber, capacity: number) => void;

  // Helpers
  /** カードが潜航可能か判定 */
  canSubmergeCard: (
    player: PlayerNumber,
    card: HandCard,
    turn: number,
    allowWaterAttribute?: boolean
  ) => boolean;
  /** 深海カードが浮上可能か判定 */
  canSurfaceCard: (
    player: PlayerNumber,
    deepSeaIndex: number,
    activeSP: number,
    emptyFieldSlots: number,
    costReduction?: number
  ) => boolean;
  /** プレイヤーの深海領域を取得 */
  getPlayerDeepSeaZone: (player: PlayerNumber) => DeepSeaZone;
  /** 潜航可能な手札カードを取得 */
  getSubmergibleCards: (
    player: PlayerNumber,
    hand: HandCard[],
    turn: number,
    allowWaterAttribute?: boolean
  ) => HandCard[];
  /** 浮上可能な深海カードを取得 */
  getSurfaceableCards: (
    player: PlayerNumber,
    activeSP: number,
    emptyFieldSlots: number,
    costReduction?: number
  ) => DeepSeaCard[];
  /** フィールドに暗黒水域持ちがいるかチェック */
  hasAnkokuSuiiki: (field: (MonsterInstance | null)[]) => boolean;
  /** 暗黒水域による追加深度を計算 */
  calculateAnkokuSuiikiBonusDepth: (field: (MonsterInstance | null)[]) => number;

  // Reset
  resetAll: () => void;
}

// ========================================
// Hook実装
// ========================================

export const useDeepSea = (): DeepSeaReturn => {
  // ========================================
  // State
  // ========================================
  const [p1DeepSeaZone, setP1DeepSeaZone] = useState<DeepSeaZone>(createDeepSeaZone());
  const [p2DeepSeaZone, setP2DeepSeaZone] = useState<DeepSeaZone>(createDeepSeaZone());
  const [showDeepSeaModal, setShowDeepSeaModal] = useState<PlayerNumber | null>(null);

  // ========================================
  // Helper: プレイヤー別の取得
  // ========================================
  const getPlayerDeepSeaZone = useCallback((player: PlayerNumber): DeepSeaZone => {
    return player === 1 ? p1DeepSeaZone : p2DeepSeaZone;
  }, [p1DeepSeaZone, p2DeepSeaZone]);

  const getSetterForPlayer = useCallback((player: PlayerNumber) => {
    return player === 1 ? setP1DeepSeaZone : setP2DeepSeaZone;
  }, []);

  // ========================================
  // Actions
  // ========================================

  /**
   * 深海領域を開放
   */
  const unlockDeepSea = useCallback((player: PlayerNumber): void => {
    const setter = getSetterForPlayer(player);
    setter((prev) => unlockDeepSeaZone(prev));
  }, [getSetterForPlayer]);

  /**
   * カードを潜航させる
   */
  const handleSubmerge = useCallback((
    player: PlayerNumber,
    card: HandCard,
    turn: number,
    initialDepth: number = 1,
    removeFromHand?: (card: HandCard) => void,
    addLog?: (message: string, type?: 'info' | 'damage' | 'heal') => void
  ): boolean => {
    const zone = getPlayerDeepSeaZone(player);

    // 潜航実行
    const result = submergeCard(zone, card, turn, initialDepth);

    if (result.success) {
      // 深海領域を更新
      const setter = getSetterForPlayer(player);
      setter(result.updatedZone);

      // 手札から削除
      if (removeFromHand) {
        removeFromHand(card);
      }

      // ログ追加
      if (addLog) {
        addLog(result.message, 'info');
      }
    } else if (addLog) {
      addLog(result.message, 'info');
    }

    return result.success;
  }, [getPlayerDeepSeaZone, getSetterForPlayer]);

  /**
   * カードを浮上させる
   */
  const handleSurface = useCallback((
    player: PlayerNumber,
    deepSeaIndex: number,
    fieldSlot: number,
    costReduction: number,
    placeOnField: (card: CardWithId, slot: number, bonus: SurfaceBonus) => boolean,
    deductSP: (amount: number) => void,
    addLog?: (message: string, type?: 'info' | 'damage' | 'heal') => void
  ): boolean => {
    const zone = getPlayerDeepSeaZone(player);

    // 浮上実行
    const result = surfaceCard(zone, deepSeaIndex, costReduction);

    if (result.success && result.surfacedCard) {
      // フィールドに配置
      const placed = placeOnField(result.surfacedCard, fieldSlot, result.bonus);

      if (placed) {
        // SP消費
        deductSP(result.spCost);

        // 深海領域を更新
        const setter = getSetterForPlayer(player);
        setter(result.updatedZone);

        // ログ追加
        if (addLog) {
          addLog(result.message, 'info');
        }

        return true;
      }
    }

    if (!result.success && addLog) {
      addLog(result.message, 'info');
    }

    return false;
  }, [getPlayerDeepSeaZone, getSetterForPlayer]);

  /**
   * 深度を増加（エンドフェイズで呼び出し）
   */
  const handleIncrementDepth = useCallback((
    player: PlayerNumber,
    bonusDepth: number = 0,
    addLog?: (message: string, type?: 'info' | 'damage' | 'heal') => void
  ): void => {
    const zone = getPlayerDeepSeaZone(player);

    if (zone.cards.length === 0) return;

    const result = incrementDepth(zone, bonusDepth);

    // 深海領域を更新
    const setter = getSetterForPlayer(player);
    setter(result.updatedZone);

    // ログ追加
    if (addLog && result.incrementedCount > 0) {
      const totalIncrement = 1 + bonusDepth;
      const bonusText = bonusDepth > 0 ? `（暗黒水域 +${bonusDepth}）` : '';
      addLog(`深海領域のカード${result.incrementedCount}枚の深度が${totalIncrement}上昇${bonusText}`, 'info');
    }
  }, [getPlayerDeepSeaZone, getSetterForPlayer]);

  /**
   * 深海領域の上限を変更
   */
  const setCapacity = useCallback((player: PlayerNumber, capacity: number): void => {
    const setter = getSetterForPlayer(player);
    setter((prev) => setDeepSeaCapacity(prev, capacity));
  }, [getSetterForPlayer]);

  // ========================================
  // Helpers
  // ========================================

  /**
   * カードが潜航可能か判定
   */
  const canSubmergeCard = useCallback((
    player: PlayerNumber,
    card: HandCard,
    turn: number,
    allowWaterAttribute: boolean = false
  ): boolean => {
    const zone = getPlayerDeepSeaZone(player);
    const context: SubmergeContext = {
      deepSeaZone: zone,
      allowWaterAttribute,
      currentTurn: turn,
    };
    return canSubmerge(card, context);
  }, [getPlayerDeepSeaZone]);

  /**
   * 深海カードが浮上可能か判定
   */
  const canSurfaceCard = useCallback((
    player: PlayerNumber,
    deepSeaIndex: number,
    activeSP: number,
    emptyFieldSlots: number,
    costReduction: number = 0
  ): boolean => {
    const zone = getPlayerDeepSeaZone(player);
    if (deepSeaIndex < 0 || deepSeaIndex >= zone.cards.length) return false;

    const deepSeaCard = zone.cards[deepSeaIndex];
    const context: SurfaceContext = {
      deepSeaZone: zone,
      activeSP,
      emptyFieldSlots,
      costReduction,
    };
    return canSurface(deepSeaCard, context);
  }, [getPlayerDeepSeaZone]);

  /**
   * 潜航可能な手札カードを取得
   */
  const getSubmergibleCards = useCallback((
    player: PlayerNumber,
    hand: HandCard[],
    turn: number,
    allowWaterAttribute: boolean = false
  ): HandCard[] => {
    return hand.filter((card) => canSubmergeCard(player, card, turn, allowWaterAttribute));
  }, [canSubmergeCard]);

  /**
   * 浮上可能な深海カードを取得
   */
  const getSurfaceableCards = useCallback((
    player: PlayerNumber,
    activeSP: number,
    emptyFieldSlots: number,
    costReduction: number = 0
  ): DeepSeaCard[] => {
    const zone = getPlayerDeepSeaZone(player);
    return zone.cards.filter((_, index) =>
      canSurfaceCard(player, index, activeSP, emptyFieldSlots, costReduction)
    );
  }, [getPlayerDeepSeaZone, canSurfaceCard]);

  /**
   * フィールドに暗黒水域持ちがいるかチェック
   */
  const hasAnkokuSuiiki = useCallback((field: (MonsterInstance | null)[]): boolean => {
    return field.some((monster) => monster && hasAnkokuSuiikiKeyword(monster));
  }, []);

  /**
   * 暗黒水域による追加深度を計算
   */
  const calculateAnkokuSuiikiBonusDepth = useCallback((field: (MonsterInstance | null)[]): number => {
    // 暗黒水域1体につき+1深度
    return field.filter((monster) => monster && hasAnkokuSuiikiKeyword(monster)).length;
  }, []);

  // ========================================
  // Reset
  // ========================================
  const resetAll = useCallback((): void => {
    setP1DeepSeaZone(createDeepSeaZone());
    setP2DeepSeaZone(createDeepSeaZone());
    setShowDeepSeaModal(null);
  }, []);

  // ========================================
  // Return
  // ========================================
  return {
    // State
    p1DeepSeaZone,
    p2DeepSeaZone,
    showDeepSeaModal,

    // Setters
    setP1DeepSeaZone,
    setP2DeepSeaZone,
    setShowDeepSeaModal,

    // Actions
    unlockDeepSea,
    handleSubmerge,
    handleSurface,
    handleIncrementDepth,
    setCapacity,

    // Helpers
    canSubmergeCard,
    canSurfaceCard,
    getPlayerDeepSeaZone,
    getSubmergibleCards,
    getSurfaceableCards,
    hasAnkokuSuiiki,
    calculateAnkokuSuiikiBonusDepth,

    // Reset
    resetAll,
  };
};

export default useDeepSea;
